import { PLACEHOLDER_VALUE } from '@/constants';
import type { InstrumentRunDataRow } from '@/types';
import Table from '@nautilusbio/nb-design-system/Table';
import type { CellClickedEvent, CellValueChangedEvent, ColDef, ICellRendererParams } from 'ag-grid-enterprise';
import type { AgGridReact } from 'ag-grid-react';
import clsx from 'clsx';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';

import tableStyles from '../../../../styles/components/table.module.css';

import { OrganizationNameCellRenderer } from './OrganizationNameCellRenderer';
import { createEmptyRows } from './reducer';
import { StudyNameCellRenderer } from './StudyNameCellRenderer';
import styles from './styles.module.css';

interface SampleTableProps {
  rowData: InstrumentRunDataRow[];
  allRowData?: InstrumentRunDataRow[];
  onRowDataChange?: (data: InstrumentRunDataRow[]) => void;
  updateSampleAt?: (rowIndex: number, patch: Partial<InstrumentRunDataRow>) => void;
  onBulkApply?: (rows: InstrumentRunDataRow[]) => void;
  /** When true, highlights cells with missing required values in danger red. */
  showCellErrors?: boolean;
  readOnlyColumns?: string[];
  showCopyToRows?: boolean;
}

const SampleTable: FC<SampleTableProps> = ({
  rowData,
  allRowData,
  onRowDataChange,
  updateSampleAt,
  onBulkApply,
  showCellErrors = false,
  readOnlyColumns = [],
  showCopyToRows = false,
}) => {
  const { t } = useTranslation(['create', 'general', 'sampleManager']);
  const gridRef = useRef<AgGridReact<InstrumentRunDataRow>>(null);

  // Local state for row data to enable inline editing
  const [localRowData, setLocalRowData] = useState<InstrumentRunDataRow[]>(() =>
    rowData.length === 0 ? createEmptyRows() : rowData,
  );

  // Determine if pagination is needed (more than 12 rows)
  const usePagination = localRowData.length > 12;

  // Sync localRowData whenever the rowData prop genuinely changes (e.g. resize,
  // CSV upload, draft load). Cell edits never change the rowData prop because
  // SampleManager.state.rowData is only updated via dispatch — so there is no
  // risk of accidentally overwriting in-progress edits here.
  useEffect(() => {
    if (rowData.length === 0) {
      setLocalRowData(createEmptyRows());
      return;
    }
    setLocalRowData(rowData);
  }, [rowData]);

  // Handle cell value changes
  const onCellValueChanged = useCallback(
    (event: CellValueChangedEvent<InstrumentRunDataRow>) => {
      const updatedData = [...localRowData];
      const rowIndex = event.rowIndex;

      if (rowIndex !== undefined && rowIndex !== null && rowIndex >= 0 && event.data) {
        // Create a new object to avoid shared references
        updatedData[rowIndex] = { ...event.data };
        setLocalRowData(updatedData);

        // Notify parent component of changes
        if (onRowDataChange) {
          // Filter out completely empty rows before notifying parent
          // A row is non-empty if it has any field filled (demux_index can be 0, which is valid)
          const nonEmptyRows = updatedData.filter(row => {
            // A row is non-empty if any primary field is filled OR the control is set
            const hasPrimary = row.name || row.study_name || row.org_name || row.well_position;
            const hasControl = row.is_control === true || (row.control_type && row.control_type !== '');

            return Boolean(hasPrimary) || Boolean(hasControl);
          });

          onRowDataChange(nonEmptyRows);
        }
      }
    },
    [localRowData, onRowDataChange],
  );

  // Column Definitions with editable configuration
  const colDefs = useMemo<ColDef<InstrumentRunDataRow>[]>(() => {
    const isReadOnly = (field: string) => readOnlyColumns.includes(field);
    const readOnlyClass = (field: string) => (isReadOnly(field) ? styles.readOnlyCell : undefined);

    return [
      {
        field: 'name',
        headerName: t('general:name'),
        editable: () => !isReadOnly('name'),
        cellClass: readOnlyClass('name'),
        cellEditor: 'agTextCellEditor',
        cellEditorParams: { maxLength: 100 },
        cellClassRules: {
          [styles.cellError]: params =>
            showCellErrors &&
            (!params.value || String(params.value).trim() === '' || params.value === PLACEHOLDER_VALUE),
        },
      },
      {
        field: 'org_name',
        headerName: t('create:run.orgName'),
        cellClass: readOnlyClass('org_name'),
        cellRenderer: (params: ICellRendererParams<InstrumentRunDataRow>) => (
          <OrganizationNameCellRenderer {...params} allRowData={allRowData} onBulkApply={onBulkApply} />
        ),
        editable: false,
        cellClassRules: {
          [styles.cellError]: params => showCellErrors && (!params.value || params.value === PLACEHOLDER_VALUE),
        },
      },
      {
        field: 'study_name',
        headerName: t('create:run.studyName'),
        cellClass: readOnlyClass('study_name'),
        editable: false,
        cellRenderer: (params: ICellRendererParams<InstrumentRunDataRow>) => (
          <StudyNameCellRenderer
            {...params}
            allRowData={allRowData}
            onBulkApply={onBulkApply}
            showCopyToRows={showCopyToRows && !isReadOnly('study_name')}
            updateSampleAt={updateSampleAt}
          />
        ),
        cellClassRules: {
          [styles.cellError]: params => showCellErrors && (!params.value || params.value === PLACEHOLDER_VALUE),
        },
      },
      {
        cellClass: clsx(tableStyles.tableCellCenter, readOnlyClass('well_position')),
        field: 'well_position',
        headerName: t('create:run.wellPosition'),
        editable: () => !isReadOnly('well_position'),
        cellEditor: 'agTextCellEditor',
        cellEditorParams: { maxLength: 2 },
        cellClassRules: {
          [styles.cellError]: params => showCellErrors && (!params.value || params.value === PLACEHOLDER_VALUE),
        },
        width: 80,
      },
      {
        cellClass: clsx(tableStyles.tableCellCenter, readOnlyClass('demux_index')),
        field: 'demux_index',
        headerName: t('create:run.demuxIndex'),
        editable: () => !isReadOnly('demux_index'),
        cellEditor: 'agNumberCellEditor',
        cellEditorParams: { min: 0, precision: 0 },
        valueParser: params => {
          const value = params.newValue;
          return value === '' || value === null ? 0 : Number(value);
        },
        width: 80,
      },
      {
        cellClass: clsx(tableStyles.tableCellCenter, readOnlyClass('flow_cell')),
        field: 'flow_cell',
        headerName: t('sampleManager:flow_cell'),
        editable: false,
        width: 90,
      },
      {
        cellClass: clsx(tableStyles.tableCellCenter, readOnlyClass('lane')),
        field: 'lane',
        headerName: t('sampleManager:lane'),
        editable: false,
        width: 70,
      },
      {
        field: 'library_group',
        headerName: t('sampleManager:library_group'),
        cellClass: readOnlyClass('library_group'),
        editable: () => !isReadOnly('library_group'),
        cellEditor: 'agTextCellEditor',
      },
      {
        field: 'sample_group',
        headerName: t('sampleManager:sample_group'),
        cellClass: readOnlyClass('sample_group'),
        editable: () => !isReadOnly('sample_group'),
        cellEditor: 'agTextCellEditor',
      },
    ];
  }, [t, updateSampleAt, onBulkApply, allRowData, showCellErrors, readOnlyColumns, showCopyToRows]);

  // Default column definition for all columns
  const defaultColDef = useMemo<ColDef>(
    () => ({
      resizable: true,
      sortable: true,
      filter: true,
    }),
    [],
  );

  // Single-click starts editing for all editable cells
  const onCellClicked = useCallback((event: CellClickedEvent<InstrumentRunDataRow>) => {
    if (event.rowIndex !== null && event.rowIndex !== undefined && event.column) {
      const colId = event.column.getColId();
      event.api.startEditingCell({
        rowIndex: event.rowIndex,
        colKey: colId,
      });
    }
  }, []);

  return (
    <Table
      className={styles.sampleTable}
      columnDefs={colDefs}
      defaultColDef={defaultColDef}
      domLayout="autoHeight"
      enableAdvancedFilter={false}
      pagination={usePagination}
      paginationPageSize={12}
      paginationPageSizeSelector={false}
      rowData={localRowData}
      suppressContextMenu={true}
      onCellValueChanged={onCellValueChanged}
      onCellClicked={onCellClicked}
      ref={gridRef}
      stopEditingWhenCellsLoseFocus={true}
      tableId="sampleTable"
      enableResponsiveResize={false}
      suppressClickEdit={true}
      undoRedoCellEditing={true}
      undoRedoCellEditingLimit={20}
      autoSizeStrategy={{ type: 'fitCellContents' }}
    />
  );
};

export default SampleTable;
